<template>
  <!--
    Os apetrechos do pescador: anzol, boia e caixa de pesca (1.2).

    São EQUIPAMENTO, e a tela fala a língua do equipamento: três encaixes na
    linha, como o cantinho do caderno — o vazio mostra o desenho da peça que
    cabe nele (D4), o cheio mostra a arte com o aro da raridade. Toca-se num
    encaixe e o baú daquele slot abre embaixo, em encaixes menores; toca-se numa
    peça e a ficha diz o que ela faz, com o botão que equipa ou tira. Dois
    toques, sem arrastar (D11). O `<select>` que existia aqui era formulário,
    não jogo.

    Vive na PÁGINA do caderno, separada pelo fio tracejado das notas — e não numa
    folha empilhada por cima: o caderno já é papel, e a folha esticada tem a
    borda a 5,6% da altura enquanto o respiro dela é fixo pela largura; com o
    baú aberto o conteúdo dobra de altura e o resumo atravessava o traço.

    O "resumo" é a soma que o servidor aplica na pescaria; a tela não soma nada.
  -->
  <section class="apetrechos">
    <header class="apetrechos__topo">
      <FishIcon nome="anchor" tamanho="pequeno" />
      <h3 class="apetrechos__titulo">{{ $t('exploration.apetrechos.titulo') }}</h3>
    </header>
    <p class="apetrechos__chamada">{{ $t('exploration.apetrechos.subtitulo') }}</p>

    <!-- A linha: os três encaixes. O aceso é o slot que a peça escolhida no baú vai ocupar. -->
    <div class="apetrechos__linha">
      <div v-for="slot in SLOTS" :key="slot" class="apetrechos__lugar">
        <FishSlot
          :item="comoItem(equipados[slot])"
          :esboco="ESBOCO_DO_SLOT[slot]"
          :selecionado="aberto === slot"
          :aceso="!!escolhida && !escolhida.equipado && escolhida.slot === slot"
          @selecionar="abrir(slot)"
        />
        <span class="apetrechos__lugar-nome">{{ $t(`exploration.apetrechos.slots.${slot}`) }}</span>
      </div>
    </div>

    <!-- O baú do slot aberto: toda unidade daquele tipo, a equipada com o selo. -->
    <div v-if="aberto" class="apetrechos__bau">
      <p class="apetrechos__rotulo">
        {{ $t('exploration.apetrechos.bau') }} · {{ $t(`exploration.apetrechos.slots.${aberto}`) }}
      </p>
      <div v-if="doSlot(aberto).length" class="apetrechos__unidades">
        <FishSlot
          v-for="(a, i) in doSlot(aberto)"
          :key="a.playerFishingGearId"
          class="escalonado"
          :style="{ '--ordem': i }"
          :item="comoItem(a)"
          :equipado="a.equipado"
          :selecionado="!!escolhida && escolhida.playerFishingGearId === a.playerFishingGearId"
          @selecionar="escolher(a)"
          @equipar="a.equipado ? tirar(a.slot) : equipar(a)"
        />
      </div>
      <p v-else class="apetrechos__nota">{{ $t('exploration.apetrechos.semUnidade') }}</p>
    </div>

    <!-- A ficha do que foi tocado: é também o caminho SEM arrastar (D11) — o botão equipa ou tira. -->
    <div v-if="temAlgum" class="apetrechos__ficha">
      <template v-if="escolhida">
        <p class="apetrechos__ficha-nome">{{ escolhida.nome }}</p>
        <p class="apetrechos__ficha-meta">{{ bonusEmTexto(escolhida.bonus) }}</p>
        <p v-if="escolhida.descricao" class="apetrechos__ficha-descricao">{{ escolhida.descricao }}</p>
        <FishButton variante="primario" tamanho="pequeno" bloco :carregando="agindo" @click="agir">
          {{ escolhida.equipado ? $t('exploration.apetrechos.tirar') : $t('exploration.apetrechos.equipar') }}
        </FishButton>
      </template>
      <p v-else class="apetrechos__nota">{{ $t('exploration.apetrechos.ficha') }}</p>
    </div>

    <!-- O que está valendo na linha, como notas de campo — é o que o servidor aplica. -->
    <ul v-if="temAlgum" class="apetrechos__resumo">
      <li class="apetrechos__resumo-titulo">{{ $t('exploration.apetrechos.resumo') }}</li>
      <li v-for="linha in resumo" :key="linha.icone">
        <FishIcon :nome="linha.icone" tamanho="pequeno" />
        <span>{{ linha.texto }}</span>
      </li>
      <li v-if="!resumo.length" class="apetrechos__nota">{{ $t('exploration.apetrechos.resumoVazio') }}</li>
    </ul>

    <div v-if="apetrechos && !temAlgum" class="apetrechos__vazio">
      <p class="apetrechos__nota">{{ $t('exploration.apetrechos.vazio') }}</p>
      <FishButton tamanho="pequeno" icone="hammer" @click="$router.push('/exploration')">
        {{ $t('exploration.apetrechos.irParaOficina') }}
      </FishButton>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { FishButton, FishIcon, FishSlot } from '@/shared/ui';
import { arteDoApetrecho } from './arteDoApetrecho';

const SLOTS = ['anzol', 'boia', 'caixa'];

/** O desenho da peça que cabe no encaixe vazio (D4) — PNGs achatados pelo `recortar-icones.mjs`. */
const ESBOCO_DO_SLOT = { anzol: 'slot-anzol', boia: 'slot-boia', caixa: 'slot-caixa' };

/** O banco fala inglês; o aro do `FishSlot` fala a escada em português. */
const RARIDADE = {
  common: 'comum',
  uncommon: 'incomum',
  rare: 'rara',
  epic: 'epica',
  legendary: 'lendaria',
  mythic: 'mitica',
};

/** O ícone de cada bônus no resumo — os mesmos atributos das notas do caderno. */
const ICONE_DO_BONUS = { poder: 'atributo-poder', taxaDeCaptura: 'atributo-captura', economiaDeIsca: 'cais-isca' };

export default {
  name: 'PescadorApetrechos',
  components: { FishButton, FishIcon, FishSlot },
  data() {
    return { SLOTS, ESBOCO_DO_SLOT, aberto: null, escolhida: null, agindo: false };
  },
  computed: {
    ...mapState('pescador', ['apetrechos']),
    ...mapGetters('pescador', ['apetrechosPorSlot']),
    equipados() {
      return this.apetrechos?.equipados || { anzol: null, boia: null, caixa: null };
    },
    temAlgum() {
      return (this.apetrechos?.itens?.length || 0) > 0;
    },
    /** As linhas do resumo, só as que valem alguma coisa. */
    resumo() {
      return Object.entries(this.apetrechos?.resumo || {})
        .filter(([, valor]) => valor > 0)
        .map(([chave, valor]) => ({
          icone: ICONE_DO_BONUS[chave] || 'atributo-poder',
          texto: this.$t(`exploration.workshop.apetrechoBonus.${chave}`, { valor }),
        }));
    },
  },
  mounted() {
    this.$store.dispatch('pescador/carregarApetrechos').catch(() => {});
  },
  methods: {
    /** Toda unidade deste slot, equipada ou não — o baú mostra o selo em quem está na linha. */
    doSlot(slot) {
      return this.apetrechosPorSlot(slot);
    },
    /** A unidade no formato que o `FishSlot` lê. */
    comoItem(a) {
      if (!a) return null;
      return {
        id: a.playerFishingGearId,
        nome: a.nome,
        imagem: arteDoApetrecho(a),
        raridade: RARIDADE[a.raridade] || 'comum',
      };
    },
    /** Tocar num encaixe da linha abre o baú dele; tocar de novo fecha. A peça equipada já vem escolhida. */
    abrir(slot) {
      if (this.aberto === slot) {
        this.aberto = null;
        this.escolhida = null;
        return;
      }
      this.aberto = slot;
      this.escolhida = this.equipados[slot] || null;
    },
    escolher(a) {
      this.escolhida =
        this.escolhida && this.escolhida.playerFishingGearId === a.playerFishingGearId ? null : a;
    },
    bonusEmTexto(bonus) {
      if (!bonus) return '';
      return Object.entries(bonus)
        .filter(([, valor]) => valor > 0)
        .map(([chave, valor]) => this.$t(`exploration.workshop.apetrechoBonus.${chave}`, { valor }))
        .join(' · ');
    },
    agir() {
      if (!this.escolhida) return;
      return this.escolhida.equipado ? this.tirar(this.escolhida.slot) : this.equipar(this.escolhida);
    },
    async equipar(a) {
      this.agindo = true;
      try {
        await this.$store.dispatch('pescador/equiparApetrecho', a.playerFishingGearId);
        this.escolhida = null;
      } catch (error) {
        console.error('Erro ao equipar o apetrecho:', error);
      } finally {
        this.agindo = false;
      }
    },
    async tirar(slot) {
      this.agindo = true;
      try {
        await this.$store.dispatch('pescador/desequiparApetrecho', slot);
        this.escolhida = null;
      } catch (error) {
        console.error('Erro ao tirar o apetrecho:', error);
      } finally {
        this.agindo = false;
      }
    },
  },
};
</script>

<style scoped>
.apetrechos {
  display: grid;
  gap: var(--e-2);
  color: var(--tinta);
  margin-top: var(--e-2);
  padding-top: var(--e-2);
  border-top: 2px dashed var(--papel-sombra);
}

.apetrechos__topo {
  display: flex;
  align-items: center;
  gap: var(--e-1);
}

.apetrechos__titulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
}

.apetrechos__chamada,
.apetrechos__nota {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.apetrechos__nota {
  font-style: italic;
}

/* ------------------------------------------------------------------- a linha */

/* Três encaixes centrados, do tamanho dos do cantinho do caderno. O `FishSlot` preenche o pai,
 * então é a coluna que dá o tamanho. */
.apetrechos__linha {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 88px));
  justify-content: center;
  gap: var(--e-4);
  padding: var(--e-1) 0;
}

.apetrechos__lugar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.apetrechos__lugar-nome {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-align: center;
}

/* --------------------------------------------------------------------- o baú */

.apetrechos__bau,
.apetrechos__ficha,
.apetrechos__resumo {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-2);
}

.apetrechos__bau {
  display: grid;
  gap: var(--e-1);
}

.apetrechos__rotulo,
.apetrechos__resumo-titulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta);
}

.apetrechos__unidades {
  display: grid;
  grid-template-columns: repeat(auto-fill, 64px);
  gap: var(--e-2);
}

/* -------------------------------------------------------------------- a ficha */

.apetrechos__ficha {
  display: grid;
  gap: var(--e-1);
  min-height: 56px;
}

.apetrechos__ficha-nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.apetrechos__ficha-meta {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.apetrechos__ficha-descricao {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-lapis);
  font-style: italic;
}

/* ------------------------------------------------------------------- o resumo */

.apetrechos__resumo {
  list-style: none;
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.apetrechos__resumo li {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: 2px 0;
}

.apetrechos__vazio {
  display: grid;
  gap: var(--e-1);
  justify-items: start;
}
</style>
